import React, { useMemo, useState } from "react";
import { Typography, Button, Select, SelectChangeEvent, MenuItem, Box, Divider,
  DialogTitle, DialogContent, DialogActions, TextField, 
  useTheme,
  Paper} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from "notistack";
import copy from 'copy-to-clipboard';

import { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import QueryCodeEditorPanel from "components/queries/QueryCodeEditorPanel";
import APIRequest, { APIError, BuildAPIURL } from "system/APIRequest";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { ActiveWidgetRequest } from "system/widget/Widget";
import TitleCase, { toTitleCase } from "components/formatting/TitleCase";
import { WidgetBaseProps } from "../WidgetContainer";

interface WidgetExportsDialogOptions extends WidgetBaseProps {
  widgetBaseSettings?: {
    exportOptions?: string[];
  }
  exportAmounts?: string[];
  activeWidgetRequests?: Array<ActiveWidgetRequest | ActiveWidgetRequest[]>;
  onClose: () => void;
}

export default function WidgetExportsDialog(props: WidgetExportsDialogOptions): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const {activeWidgetRequests, onClose } = props;

  const [exportFormat, setExportFormat] = useState<string>("csv");
  const [apiInfoDisplayed, setApiInfoDisplayed] = useState<boolean>(false);
  const [customExportFilename, setCustomExportFilename] = useState<string | undefined>(undefined);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [selectedTopLevelRequestIndex, setSelectedTopLevelRequestIndex] = useState<number>(0);
  const [selectedActiveRequestIndex, setSelectedActiveRequestIndex] = useState<number>(0);

  const exportAmounts = props.widgetBaseSettings?.exportOptions || ["50", "100", "250", "500", "1000"];
  const [exportAmount, setExportAmount] = useState<string>(exportAmounts?.[0] || "50");

  const activeTopLevelRequest = useMemo<ActiveWidgetRequest | ActiveWidgetRequest[] | undefined>(() => {
    if (activeWidgetRequests && Array.isArray(activeWidgetRequests) && activeWidgetRequests.length > 0) {
      if (selectedTopLevelRequestIndex >= activeWidgetRequests.length) {
        setSelectedTopLevelRequestIndex(0);
      } else {
        return activeWidgetRequests[selectedTopLevelRequestIndex];
      }
    }
    return undefined;
  }, [activeWidgetRequests, selectedTopLevelRequestIndex]);

  interface ExportRequestOption {
    index: number;
    name: string;
    requests: ActiveWidgetRequest | ActiveWidgetRequest[];
  }

  const topLevelRequestOptions = useMemo<ExportRequestOption[]>(() => {
    const options: ExportRequestOption[] = [];
    if (activeWidgetRequests && Array.isArray(activeWidgetRequests)) {
      activeWidgetRequests.forEach((requestInfo, index) => {
        options.push({
          index: index,
          name: `Request ${index + 1}`,
          requests: requestInfo
        });
      });
    }
    return options;
  }, [activeWidgetRequests]);

  const activeWidgetRequest = useMemo<ActiveWidgetRequest | undefined>(() => {
    if (activeTopLevelRequest) {
      if (Array.isArray(activeTopLevelRequest)) {
        return activeTopLevelRequest[selectedActiveRequestIndex];
      }
      return activeTopLevelRequest;
    }
    return undefined;
  }, [activeTopLevelRequest, selectedActiveRequestIndex]);

  const canExportCustomAmounts = useMemo(() => {
    if (activeWidgetRequest && activeWidgetRequest.endpoint) {
      return ["records", "topics", "social-influencers"].some(type =>
        activeWidgetRequest.endpoint.endsWith(type)
      );
    }
    return false;
  }, [activeWidgetRequest]);

  const exportFilename = useMemo<string>(() => {
    if (typeof(customExportFilename) === "string") {
      return customExportFilename;
    } else if (activeWidgetRequest) {
      return `${toTitleCase(activeWidgetRequest.name)} - ${activeWidgetRequest.queryDisplayTitle}`;
    }
    return "export";
  }, [customExportFilename, activeWidgetRequest, selectedActiveRequestIndex, selectedTopLevelRequestIndex]);

  const fullEndpoint = useMemo(() => {
    if (!activeWidgetRequest || !activeWidgetRequest.endpoint) {
      return;
    }
    let requestURL = activeWidgetRequest.endpoint;
    if (activeWidgetRequest.activeQueryIds && activeWidgetRequest.activeQueryIds.length > 0) {
      // if the last character is not a slash, add one
      if (!requestURL.endsWith('/')) {
        requestURL += '/';
      }
      // append the active query IDs to the endpoint
      requestURL += activeWidgetRequest.activeQueryIds.join(',');
    }

    let queryParams = activeWidgetRequest.queryParams || {};
    if (typeof queryParams === "string") {
      if (queryParams && queryParams.length > 0) {
        queryParams += "&";
      }
      queryParams = `${queryParams}format=${exportFormat}`;
    } else if (typeof queryParams === "object") {
      queryParams = {
        ...(queryParams || {}),
        format: exportFormat
      };
    }
    return BuildAPIURL(requestURL, queryParams);
  }, [activeWidgetRequest]);

  const requestInit = useMemo(() => {
    if (!activeWidgetRequest) {
      return;
    }

    const requestInit = { ...(activeWidgetRequest.requestInit || {}) } as RequestInit;

    // Default to POST if no method is specified
    if (requestInit && !requestInit.method) {
      requestInit.method = 'POST';
    }

    if (canExportCustomAmounts && requestInit.body) {
      const { records, topics, options } = requestInit.body;
      if (records) records.size = parseInt(exportAmount);
      if (topics) topics.size = parseInt(exportAmount);
      if (options) options.size = parseInt(exportAmount);
    }

    let body = requestInit.body;

    // Ensure the requestInit has a stringified body
    if (requestInit && body && typeof body !== 'string') {
      body = JSON.stringify(body, undefined, 2);
    }

    const requestInit2 = {
      body: body,
      method: requestInit.method || 'POST'
    };

    return requestInit2;
  }, [exportAmount, exportAmounts, activeWidgetRequest, canExportCustomAmounts]);

  const getExportFile = () => {
    if (!activeWidgetRequest || !fullEndpoint || !requestInit) {
      setIsExporting(false);
      return;
    }

    APIRequest(fullEndpoint, {}, requestInit, false, 3, [400, 401]).then((response) => {
      // Read the response using a stream reader
      const body = response.body;
      const reader = body.getReader();
      const responseChunks: Uint8Array[] = [];

      // Read the stream and convert it to a blob
      function readResponseStream() {
        return reader.read().then(({ done, value }: { done: boolean; value: Uint8Array }) => {
          if (done) {
            // We've completed the download of the file
            const blob = new Blob(responseChunks, {type: `text/${exportFormat}`});
            // Download the blob as a file
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${exportFilename}.${exportFormat}`;
            const appendedChild = document.body.appendChild(a);
            appendedChild.click();
            setTimeout(() => {
              // remove the a element from the page and clear the object URL to save memory
              document.body.removeChild(appendedChild);
              URL.revokeObjectURL(url);
            }, 5000);
            enqueueSnackbar(i18n.t("widgets.download-export-success"), {variant: "success"});
            onClose();
            setIsExporting(false);
          } else {
            responseChunks.push(value);
            return readResponseStream();
          }
        });
      }

      // Read the stream
      return readResponseStream();
    }).catch((e: APIError) => {
      enqueueSnackbar(i18n.t("widgets.download-export-failure", {errorMessage: e.message}), {variant: "error", persist: true});
      setIsExporting(false);
    });
  };

  const hasMultipleRequests = useMemo(() => {
    let hasMultipleRequests = false;
    if (activeWidgetRequests && activeWidgetRequests.length > 1) {
      hasMultipleRequests = true;
    }
    if (activeTopLevelRequest && Array.isArray(activeTopLevelRequest)) {
      hasMultipleRequests = activeTopLevelRequest.length > 1;
    }
    return hasMultipleRequests;
  }, [activeWidgetRequests, selectedActiveRequestIndex, activeTopLevelRequest]);

  return (
    <ResponsiveDialog
      maxWidth="sm"
      open={true}
      onClose={props.onClose}>
      <DialogTitle>
        <Typography variant="h4">
          {i18n.t("widgets.export-options-dialog")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }} >
        <Paper sx={{ p: 2 }}>
        {hasMultipleRequests && (
          <Box>
            {(activeWidgetRequests && activeWidgetRequests?.length > 1) && (
              <Box sx={{ mb: 2 }}>
                <SimpleFormLabel>{i18n.t("widgets.select-request-type")}</SimpleFormLabel>
                <Select
                  value={String(selectedTopLevelRequestIndex)}
                  fullWidth
                  onChange={(e: SelectChangeEvent) => {
                    setCustomExportFilename(undefined);
                    setSelectedTopLevelRequestIndex(parseInt(e.target.value));
                  }}>
                  {topLevelRequestOptions.map((requestInfo, index) => {
                    return (
                      <MenuItem key={index} value={index}>
                        <TitleCase text={requestInfo.name} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            )}
            {(activeTopLevelRequest && Array.isArray(activeTopLevelRequest) && activeTopLevelRequest.length > 1) && (
              <Box sx={{ mb: 2 }}>
                <SimpleFormLabel>{i18n.t("widgets.select-request")}</SimpleFormLabel>
                <Select
                  fullWidth
                  value={String(selectedActiveRequestIndex)}
                  onChange={(e: SelectChangeEvent) => {
                    setCustomExportFilename(undefined);
                    setSelectedActiveRequestIndex(parseInt(e.target.value));
                  }}>
                  {activeTopLevelRequest.map((requestInfo, index) => {
                    return (
                      <MenuItem key={index} value={index}>
                        <TitleCase text={requestInfo.queryDisplayTitle} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            )}
          </Box>
        )}
        <Box sx={{display:"grid", gridTemplateColumns: "auto 130px", columnGap: 2}}>
          <Box>
            <SimpleFormLabel>{i18n.t("widgets.export-filename")}</SimpleFormLabel>
            <SimpleFormTextField
              value={customExportFilename || exportFilename}
              onChange={(e) => {
                const newFilename = e.target.value;
                if (newFilename === exportFilename) {
                  setCustomExportFilename(undefined);
                } else {
                  setCustomExportFilename(newFilename);
                }
              }} />
          </Box>
          <Box>
            <SimpleFormLabel>{i18n.t("widgets.export-format")}</SimpleFormLabel>
            <Select
              label={i18n.t("widgets.export-format")}
              defaultValue="csv"
              fullWidth
              onChange={(e: SelectChangeEvent) => {
                setExportFormat(e.target.value);
              }}
              value={exportFormat}>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="tsv">TSV</MenuItem>
            </Select>
          </Box>
        </Box>
        {canExportCustomAmounts &&
          <Box sx={{ mb: 2 }}>
            <SimpleFormLabel>{i18n.t("widgets.export-amount")}</SimpleFormLabel>
            <Select
              label={i18n.t("widgets.export-amount")}
              onChange={(e: SelectChangeEvent) => {
                setExportAmount(e.target.value);
              }}
              value={exportAmount}>
              {exportAmounts.map((amount) => (
                <MenuItem key={amount} value={amount}>{amount}</MenuItem>
              ))}
            </Select>
          </Box>
        }
        {apiInfoDisplayed &&
          <>
            <Divider />
            <SimpleFormLabel
              actions={
                <Button
                  variant="text"
                  size="small"
                  sx={{mt:"-5px"}}
                  onClick={() => {if (fullEndpoint) {copy(fullEndpoint)}}}>
                  {i18n.t("copy")}
                </Button>
              }
            >
              {i18n.t("widgets.request-url")}
            </SimpleFormLabel>
            <TextField autoComplete="off" id="outlined-basic" variant="outlined" fullWidth value={fullEndpoint} aria-readonly />
            <SimpleFormLabel
                actions={
                <Button
                  variant="text"
                  size="small"
                  sx={{mt:"-5px"}}
                  onClick={() => {if (requestInit?.body) {copy(requestInit?.body)}}}>
                  {i18n.t("copy")}
                </Button>
              }
            >
              {i18n.t("widgets.request-body")}
            </SimpleFormLabel>
            {requestInit?.body &&
              <QueryCodeEditorPanel height={200} content={requestInit?.body} readOnly={true}></QueryCodeEditorPanel>
            }
          </>
        }
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setApiInfoDisplayed(!apiInfoDisplayed)}>
          {i18n.t("widgets.show-api-info", {hide_show: apiInfoDisplayed ? i18n.t("hide") : i18n.t("show")})}
        </Button>
        <Button
          onClick={props.onClose}
          variant="contained"
          color="secondary"
        >
          {i18n.t("cancel")}
        </Button>
        <Button
          onClick={() => { setIsExporting(true); getExportFile(); }}
          loading={isExporting}
          variant="contained"
        >
          {i18n.t("widgets.export")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
